'use client';

import Link from 'next/link';
import React from 'react';
import { useTranslations } from 'next-intl';
import { PlatformLogo } from '@/components/icons/platform-logo';
import { useMarketingNavLinks } from '@/hooks/use-marketing-nav-links';
import { cn } from '@/lib/utils';

const sections = [
  {
    id: 'personal-data-collection',
    title: 'Personal Data We Collect',
    color: 'blue',
  },
  { id: 'how-we-use-data', title: 'How We Use Personal Data', color: 'green' },
  { id: 'disclosure', title: 'Disclosure of Personal Data', color: 'orange' },
  {
    id: 'international-transfers',
    title: 'International Data Transfers',
    color: 'red',
  },
  { id: 'retention', title: 'Retention', color: 'purple' },
  { id: 'cookies', title: 'Cookies and Similar Technologies', color: 'indigo' },
  { id: 'security', title: 'Security', color: 'teal' },
  { id: 'children', title: 'Children', color: 'pink' },
  { id: 'your-rights', title: 'Your Rights and Choices', color: 'yellow' },
  {
    id: 'links-third-parties',
    title: 'Links and Third Parties',
    color: 'gray',
  },
  {
    id: 'business-accounts',
    title: 'Business Accounts and Teams',
    color: 'blue',
  },
  { id: 'policy-changes', title: 'Changes to This Policy', color: 'green' },
  { id: 'contact-us', title: 'Contact Us', color: 'orange' },
] as const;

const addendumSections = [
  {
    id: 'controller-information',
    title: 'Controller Information',
    color: 'red',
  },
  { id: 'legal-bases', title: 'Legal Bases for Processing', color: 'purple' },
  {
    id: 'addendum-transfers',
    title: 'International Transfers',
    color: 'indigo',
  },
  { id: 'addendum-retention', title: 'Retention', color: 'teal' },
  {
    id: 'data-protection-rights',
    title: 'Your Data Protection Rights',
    color: 'pink',
  },
  {
    id: 'third-party-processing',
    title: 'Processing by Third Parties',
    color: 'yellow',
  },
  {
    id: 'automated-decision',
    title: 'Automated Decision-Making and Profiling',
    color: 'gray',
  },
  { id: 'security-processing', title: 'Security of Processing', color: 'blue' },
  { id: 'children-data', title: "Children's Data", color: 'green' },
  {
    id: 'addendum-updates',
    title: 'Updates to This Addendum',
    color: 'orange',
  },
  { id: 'addendum-contact', title: 'Contact and Complaints', color: 'red' },
] as const;

export default function PrivacyClient() {
  const { navLinks } = useMarketingNavLinks();
  const t = useTranslations('MarketingUI');
  const [active, setActive] = React.useState<string>(sections[0]!.id);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e && e.isIntersecting) {
            setActive((e.target as HTMLElement).id);
          }
        }
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] }
    );

    const allSections = [...sections, ...addendumSections];
    const nodes = allSections
      .map((s) => document.getElementById(s.id))
      .filter(Boolean) as Element[];
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  const handleSmoothScroll = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  };

  return (
    <div className="bg-background min-h-dvh">
      <nav
        className="fixed inset-x-0 top-0 z-50 grid grid-cols-2 items-center px-5 py-3 md:grid-cols-[1fr_auto_1fr] md:px-8"
        style={{
          fontFamily: "'DM Sans', sans-serif",
          background: '#ffffff',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Link href="/" aria-label="Home">
          <span style={{ color: '#000000' }}>
            <PlatformLogo size={24} />
          </span>
        </Link>

        <div className="hidden items-center justify-center gap-7 md:flex">
          {navLinks.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              className="rounded-lg px-3 py-1.5 text-[15px] font-normal transition-all duration-200 hover:bg-black/5"
              style={{
                color: '#000000',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center justify-end gap-1.5 md:gap-2">
          <Link
            href="/signup"
            className="inline-flex items-center rounded-full px-3 py-1.5 text-[11px] font-medium whitespace-nowrap transition-colors duration-300 md:px-5 md:py-2 md:text-[14px]"
            style={{
              background: '#ffffff',
              color: '#000000',
              border: '1px solid rgba(0,0,0,0.1)',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {t('signUp')}
          </Link>
          <Link
            href="/login"
            className="inline-flex items-center rounded-full px-3 py-1.5 text-[11px] font-medium whitespace-nowrap transition-colors duration-300 md:px-5 md:py-2 md:text-[14px]"
            style={{
              background: '#000000',
              color: '#ffffff',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {t('logIn')}
          </Link>
        </div>
      </nav>

      <div className="container mx-auto max-w-6xl px-4 pt-28 pb-10">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="border-border bg-muted/40 text-foreground/80 mb-4 inline-flex items-center rounded-full border px-4 py-1.5 text-xs shadow-sm backdrop-blur transition-colors">
            <div className="mr-2 h-1.5 w-1.5 rounded-full bg-green-500" />
            Last updated: October 30, 2025
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
          <p className="text-muted-foreground mt-1 text-base">
            This Privacy Policy describes how IMAI Studio Inc collects, uses,
            discloses, and protects information relating to users of our
            website, applications, and services.
          </p>
        </div>

        {/* Introduction */}
        <div className="border-border/60 bg-muted/40 mb-8 rounded-lg border p-6">
          <p className="text-base">
            IMAI Studio Inc is a Delaware-registered corporation with its
            principal office at 1388 Haight Street, San Francisco, CA 94117,
            USA.
          </p>
          <p className="mt-2 text-base">
            If you have any questions, you may contact us at
            privacy@imai.studio.
          </p>
          <p className="mt-2 text-base font-semibold">
            By using our Services, you agree to this Privacy Policy and our
            Terms of Service.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          {/* TOC */}
          <aside className="lg:col-span-1">
            <div className="border-border/60 bg-card sticky top-8 rounded-xl border p-4 shadow-sm">
              <div className="mb-2 text-sm font-semibold">
                Table of Contents
              </div>
              <nav className="space-y-1">
                {sections.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={handleSmoothScroll(s.id)}
                    className={cn(
                      'block rounded-md px-3 py-2 text-sm transition-colors',
                      active === s.id
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {i + 1}. {s.title}
                  </a>
                ))}
                <div className="border-border/60 my-4 border-t" />
                <div className="text-muted-foreground mb-2 text-xs font-semibold uppercase">
                  EEA/UK/Switzerland Addendum
                </div>
                {addendumSections.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={handleSmoothScroll(s.id)}
                    className={cn(
                      'block rounded-md px-3 py-2 text-sm transition-colors',
                      active === s.id
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {i + 1}. {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-3">
            <div className="space-y-8">
              <Section
                id="personal-data-collection"
                index={1}
                color="blue"
                title="Personal Data We Collect"
              >
                <p>
                  We collect information (&quot;Personal Data&quot;) that
                  identifies or relates to you as follows:
                </p>

                <h3 className="mt-6 mb-2 text-base font-semibold">
                  A. Information You Provide
                </h3>
                <p className="mb-2 font-semibold">Account Information:</p>
                <p>
                  When you create an account, we collect your name, email,
                  password, and other profile details. If you subscribe or make
                  a payment, we may collect billing information such as
                  cardholder name, payment method, and transaction history
                  (processed by our payment partner, Stripe).
                </p>
                <p className="mt-4 mb-2 font-semibold">User Content:</p>
                <p>
                  We collect data that you upload or input, including product
                  images, prompts, files, 3D assets, and other creative
                  materials you use in the platform.
                </p>
                <p className="mt-4 mb-2 font-semibold">Communication Data:</p>
                <p>
                  If you contact us (by email or social media), we collect your
                  name, contact details, and the content of your communication.
                </p>
                <p className="mt-4 mb-2 font-semibold">Other Voluntary Data:</p>
                <p>
                  You may provide additional data through surveys, feedback, or
                  participation in events.
                </p>

                <h3 className="mt-6 mb-2 text-base font-semibold">
                  B. Information Collected Automatically
                </h3>
                <p className="mb-2">
                  When you access or use our Services, we collect:
                </p>
                <ul className="bg-muted/40 mt-4 grid grid-cols-1 gap-2 rounded-lg p-4">
                  <li className="text-sm">
                    <strong>Log and Usage Data:</strong> IP address, browser
                    type, device identifiers, access times, pages viewed, and
                    actions taken.
                  </li>
                  <li className="text-sm">
                    <strong>Device and Technical Data:</strong> Operating
                    system, screen resolution, and system performance data.
                  </li>
                  <li className="text-sm">
                    <strong>Location Data:</strong> We determine approximate
                    location from your IP address to ensure service reliability
                    and detect abuse.
                  </li>
                  <li className="text-sm">
                    <strong>Cookies and Similar Technologies:</strong> Used to
                    operate, secure, and improve the Services (see &quot;Cookie
                    Notice&quot; below).
                  </li>
                </ul>

                <h3 className="mt-6 mb-2 text-base font-semibold">
                  C. Information from Third Parties
                </h3>
                <p>We may receive information from:</p>
                <ul className="bg-muted/40 mt-4 grid grid-cols-1 gap-2 rounded-lg p-4">
                  <li className="text-sm">
                    Service partners (e.g., hosting, analytics, or fraud
                    prevention providers);
                  </li>
                  <li className="text-sm">
                    AI model partners (OpenAI, Replicate, Fal.ai, etc.) to
                    process your content;
                  </li>
                  <li className="text-sm">
                    Public or commercial sources for security, analytics, and
                    compliance.
                  </li>
                </ul>
              </Section>

              <Section
                id="how-we-use-data"
                index={2}
                color="green"
                title="How We Use Personal Data"
              >
                <p>We use Personal Data to:</p>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {[
                    'Operate and provide the Services to you',
                    'Authenticate and manage your account',
                    'Process payments and transactions',
                    'Communicate with you about updates or support',
                    'Detect, prevent, and address security or abuse',
                    'Analyze and improve our Services',
                    'Comply with applicable laws and obligations',
                  ].map((t) => (
                    <div key={t} className="bg-primary/5 rounded-lg p-3">
                      <span className="text-sm font-medium">{t}</span>
                    </div>
                  ))}
                </div>

                <h3 className="mt-6 mb-2 text-base font-semibold">
                  Model Training:
                </h3>
                <p>
                  IMAI Studio does not directly train or fine-tune its AI models
                  on user-generated content. However, content may be processed
                  through third-party AI providers (such as OpenAI, Replicate,
                  or Fal.ai) solely to generate requested outputs. These
                  providers may have their own privacy policies governing any
                  model training.
                </p>
              </Section>

              <Section
                id="disclosure"
                index={3}
                color="orange"
                title="Disclosure of Personal Data"
              >
                <p>
                  We may disclose Personal Data in the following circumstances:
                </p>
                <div className="mt-4 space-y-3">
                  <div className="rounded-lg border-l-4 border-orange-400 bg-orange-50/50 p-4 dark:border-orange-500 dark:bg-orange-500/10">
                    <p className="mb-1 font-semibold">
                      Vendors and Service Providers:
                    </p>
                    <p className="text-sm">
                      With trusted partners performing services for us,
                      including AWS, Vercel, Convex, Stripe, Resend, and Google
                      Analytics.
                    </p>
                  </div>
                  <div className="rounded-lg border-l-4 border-orange-400 bg-orange-50/50 p-4 dark:border-orange-500 dark:bg-orange-500/10">
                    <p className="mb-1 font-semibold">
                      AI Processing Partners:
                    </p>
                    <p className="text-sm">
                      To generate requested results, we securely transmit
                      prompts, files, and instructions to integrated AI
                      providers such as OpenAI, Replicate, or Fal.ai.
                    </p>
                  </div>
                  <div className="rounded-lg border-l-4 border-orange-400 bg-orange-50/50 p-4 dark:border-orange-500 dark:bg-orange-500/10">
                    <p className="mb-1 font-semibold">Affiliates:</p>
                    <p className="text-sm">
                      With subsidiaries or affiliates under common control, in
                      accordance with this policy.
                    </p>
                  </div>
                  <div className="rounded-lg border-l-4 border-orange-400 bg-orange-50/50 p-4 dark:border-orange-500 dark:bg-orange-500/10">
                    <p className="mb-1 font-semibold">Business Transfers:</p>
                    <p className="text-sm">
                      During any merger, acquisition, or sale of assets.
                    </p>
                  </div>
                  <div className="rounded-lg border-l-4 border-orange-400 bg-orange-50/50 p-4 dark:border-orange-500 dark:bg-orange-500/10">
                    <p className="mb-1 font-semibold">Legal and Compliance:</p>
                    <p className="text-sm">
                      When required by law, regulation, or governmental
                      authority, or to protect the rights and safety of users or
                      IMAI.
                    </p>
                  </div>
                  <div className="rounded-lg border-l-4 border-orange-400 bg-orange-50/50 p-4 dark:border-orange-500 dark:bg-orange-500/10">
                    <p className="mb-1 font-semibold">With Consent:</p>
                    <p className="text-sm">
                      When you choose to share data with collaborators or
                      publish generated content.
                    </p>
                  </div>
                </div>
              </Section>

              <Section
                id="international-transfers"
                index={4}
                color="red"
                title="International Data Transfers"
              >
                <p>
                  We store and process data in the United States and other
                  regions (including the EU and Asia) to improve speed and
                  reliability.
                </p>
                <p className="mt-4">
                  When transferring data outside your country, we rely on
                  Standard Contractual Clauses (SCCs) and equivalent lawful
                  transfer mechanisms to ensure your data receives adequate
                  protection.
                </p>
              </Section>

              <Section
                id="retention"
                index={5}
                color="purple"
                title="Retention"
              >
                <p>We retain Personal Data only as long as necessary to:</p>
                <ul className="bg-muted/40 mt-4 grid grid-cols-1 gap-2 rounded-lg p-4">
                  <li className="text-sm">
                    Provide and maintain the Services;
                  </li>
                  <li className="text-sm">Comply with legal obligations;</li>
                  <li className="text-sm">
                    Resolve disputes and enforce agreements.
                  </li>
                </ul>
                <p className="mt-4">
                  Temporary uploads and generated assets are stored per your
                  account settings and may be deleted after inactivity or
                  account closure.
                </p>
              </Section>

              <Section
                id="cookies"
                index={6}
                color="indigo"
                title="Cookies and Similar Technologies"
              >
                <p>We use cookies and related technologies for:</p>
                <ul className="bg-muted/40 mt-4 grid grid-cols-1 gap-2 rounded-lg p-4">
                  <li className="text-sm">
                    <strong>Essential operations</strong> (account
                    authentication, session management);
                  </li>
                  <li className="text-sm">
                    <strong>Analytics</strong> (to understand usage via Google
                    Analytics);
                  </li>
                  <li className="text-sm">
                    <strong>Performance and error tracking</strong> (via
                    built-in error handling).
                  </li>
                </ul>
                <p className="mt-4">
                  You can manage cookie preferences in your browser settings.
                  Refusing some cookies may impact functionality.
                </p>
              </Section>

              <Section id="security" index={7} color="teal" title="Security">
                <p>
                  We implement administrative, technical, and organizational
                  safeguards to protect your data against unauthorized access,
                  alteration, or destruction. However, no system is completely
                  secure, and we cannot guarantee absolute protection.
                </p>
              </Section>

              <Section id="children" index={8} color="pink" title="Children">
                <p>
                  Our Services are not directed to children under 13, and we do
                  not knowingly collect Personal Data from them.
                </p>
                <p className="mt-4">
                  If you believe a child has provided data, please contact
                  privacy@imai.studio for prompt removal.
                </p>
              </Section>

              <Section
                id="your-rights"
                index={9}
                color="yellow"
                title="Your Rights and Choices"
              >
                <p>Depending on your jurisdiction, you may have rights to:</p>
                <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
                  {[
                    'Access and obtain a copy of your Personal Data',
                    'Request deletion, correction, or transfer',
                    'Withdraw consent where applicable',
                    'Object to processing or marketing communications',
                  ].map((t) => (
                    <div
                      key={t}
                      className="flex items-center rounded-lg bg-yellow-500/10 p-3 text-sm dark:bg-yellow-500/15"
                    >
                      {t}
                    </div>
                  ))}
                </div>
                <p className="mt-4">
                  You can manage many settings through your account or contact
                  us at privacy@imai.studio.
                </p>
              </Section>

              <Section
                id="links-third-parties"
                index={10}
                color="gray"
                title="Links and Third Parties"
              >
                <p>
                  Our Services may link to third-party platforms or APIs. We are
                  not responsible for their privacy or data handling practices.
                  Please review their policies before sharing information.
                </p>
              </Section>

              <Section
                id="business-accounts"
                index={11}
                color="blue"
                title="Business Accounts and Teams"
              >
                <p>
                  If you join IMAI Studio through an organization (e.g.,
                  business account), your administrator may access or manage
                  your account and content in accordance with that
                  organization&apos;s policies.
                </p>
              </Section>

              <Section
                id="policy-changes"
                index={12}
                color="green"
                title="Changes to This Policy"
              >
                <p>We may update this Privacy Policy from time to time.</p>
                <p className="mt-4">
                  The &quot;Last Updated&quot; date at the top indicates the
                  effective date. Continued use of the Services after updates
                  constitutes acceptance of the revised policy.
                </p>
              </Section>

              <Section
                id="contact-us"
                index={13}
                color="orange"
                title="Contact Us"
              >
                <p>
                  <strong>IMAI Studio Inc</strong>
                </p>
                <p className="mt-2">
                  1388 Haight Street, San Francisco, CA 94117, USA
                </p>
                <p className="mt-2">Email: privacy@imai.studio</p>
                <p className="mt-4">
                  For questions, requests, or concerns about privacy, please
                  contact us at the above address.
                </p>
              </Section>

              {/* EEA/UK/Switzerland Addendum */}
              <div className="border-border/60 mt-12 border-t pt-8">
                <div className="mb-8">
                  <h2 className="mb-2 text-2xl font-bold tracking-tight">
                    Privacy Policy Addendum for EEA, UK, and Switzerland Users
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Last Updated: October 30, 2025
                  </p>
                  <p className="mt-4">
                    This Privacy Policy Addendum (&quot;Addendum&quot;)
                    supplements the IMAI Studio Global Privacy Policy and
                    applies to users located in the European Economic Area
                    (EEA), the United Kingdom (UK), and Switzerland.
                  </p>
                  <p className="mt-2">
                    It describes how IMAI Studio Inc processes your Personal
                    Data in accordance with the EU General Data Protection
                    Regulation (GDPR), the UK GDPR, and the Swiss Federal Data
                    Protection Act (FADP).
                  </p>
                </div>

                <div className="space-y-8">
                  <Section
                    id="controller-information"
                    index={1}
                    color="red"
                    title="Controller Information"
                  >
                    <p>
                      <strong>IMAI Studio Inc</strong>
                    </p>
                    <p className="mt-2">
                      1388 Haight Street
                      <br />
                      San Francisco, CA 94117, United States
                    </p>
                    <p className="mt-2">Email: privacy@imai.studio</p>
                    <p className="mt-4">
                      IMAI Studio Inc acts as the data controller for Personal
                      Data collected through our Services.
                    </p>
                    <p className="mt-2">
                      For questions or concerns about this Addendum or your data
                      rights, you may contact our Data Protection Officer via
                      privacy@imai.studio.
                    </p>
                  </Section>

                  <Section
                    id="legal-bases"
                    index={2}
                    color="purple"
                    title="Legal Bases for Processing"
                  >
                    <p>
                      We process your Personal Data only when a legal basis
                      applies under the GDPR. Depending on your interaction with
                      our Services, processing is based on one or more of the
                      following:
                    </p>
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="border-border border-b">
                            <th className="p-3 text-left font-semibold">
                              Purpose
                            </th>
                            <th className="p-3 text-left font-semibold">
                              Legal Basis
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-border/60 border-b">
                            <td className="p-3">
                              To provide, operate, and maintain our Services
                            </td>
                            <td className="p-3">
                              Performance of a contract (Art. 6(1)(b))
                            </td>
                          </tr>
                          <tr className="border-border/60 border-b">
                            <td className="p-3">
                              To authenticate and manage accounts
                            </td>
                            <td className="p-3">
                              Performance of a contract (Art. 6(1)(b))
                            </td>
                          </tr>
                          <tr className="border-border/60 border-b">
                            <td className="p-3">
                              To process payments and transactions
                            </td>
                            <td className="p-3">
                              Performance of a contract / legitimate interest
                              (Art. 6(1)(b), (f))
                            </td>
                          </tr>
                          <tr className="border-border/60 border-b">
                            <td className="p-3">
                              To communicate with you, including support
                            </td>
                            <td className="p-3">
                              Legitimate interest / consent (Art. 6(1)(a), (f))
                            </td>
                          </tr>
                          <tr className="border-border/60 border-b">
                            <td className="p-3">
                              To prevent fraud and maintain security
                            </td>
                            <td className="p-3">
                              Legitimate interest (Art. 6(1)(f))
                            </td>
                          </tr>
                          <tr className="border-border/60 border-b">
                            <td className="p-3">
                              To comply with legal obligations
                            </td>
                            <td className="p-3">
                              Legal obligation (Art. 6(1)(c))
                            </td>
                          </tr>
                          <tr className="border-border/60 border-b">
                            <td className="p-3">
                              For analytics and service improvement
                            </td>
                            <td className="p-3">
                              Legitimate interest / consent (Art. 6(1)(a), (f))
                            </td>
                          </tr>
                          <tr className="border-border/60 border-b">
                            <td className="p-3">
                              For marketing or newsletters
                            </td>
                            <td className="p-3">Consent (Art. 6(1)(a))</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-4">
                      Where consent is the legal basis, you may withdraw consent
                      at any time without affecting the lawfulness of prior
                      processing.
                    </p>
                  </Section>

                  <Section
                    id="addendum-transfers"
                    index={3}
                    color="indigo"
                    title="International Transfers"
                  >
                    <p>
                      We may transfer your Personal Data to the United States
                      and other countries outside the EEA, UK, or Switzerland
                      where data protection standards may differ.
                    </p>
                    <p className="mt-4">
                      To safeguard such transfers, IMAI Studio relies on:
                    </p>
                    <ul className="bg-muted/40 mt-4 grid grid-cols-1 gap-2 rounded-lg p-4">
                      <li className="text-sm">
                        Standard Contractual Clauses (SCCs) approved by the
                        European Commission;
                      </li>
                      <li className="text-sm">
                        The UK International Data Transfer Addendum; and
                      </li>
                      <li className="text-sm">
                        Equivalent protections recognized by Swiss authorities.
                      </li>
                    </ul>
                    <p className="mt-4">
                      A copy of these transfer mechanisms can be requested by
                      contacting privacy@imai.studio.
                    </p>
                  </Section>

                  <Section
                    id="addendum-retention"
                    index={4}
                    color="teal"
                    title="Retention"
                  >
                    <p>
                      We retain Personal Data only for as long as necessary for
                      the purposes outlined in our Global Privacy Policy,
                      including legal, accounting, or reporting requirements.
                    </p>
                    <p className="mt-4">
                      When retention is no longer necessary, data will be
                      securely deleted or anonymized.
                    </p>
                  </Section>

                  <Section
                    id="data-protection-rights"
                    index={5}
                    color="pink"
                    title="Your Data Protection Rights"
                  >
                    <p>
                      Under applicable data protection law, you have the
                      following rights:
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
                      {[
                        'Access – to obtain confirmation whether we process your Personal Data and request a copy',
                        'Rectification – to correct inaccurate or incomplete data',
                        'Erasure ("Right to be Forgotten") – to request deletion where legally permissible',
                        'Restriction – to limit how we process your data under certain conditions',
                        'Portability – to receive your data in a structured, commonly used, and machine-readable format',
                        'Objection – to object to processing based on legitimate interests or direct marketing',
                        'Withdraw Consent – where processing is based on consent',
                        'Lodge a Complaint – to contact your local data protection authority if you believe we have not complied with applicable law',
                      ].map((t) => (
                        <div
                          key={t}
                          className="flex items-start rounded-lg bg-pink-500/10 p-3 text-sm dark:bg-pink-500/15"
                        >
                          {t}
                        </div>
                      ))}
                    </div>
                    <p className="mt-4">
                      Requests can be submitted via privacy@imai.studio or
                      through any available account tools.
                    </p>
                    <p className="mt-2">
                      We may require identity verification to process your
                      request.
                    </p>
                  </Section>

                  <Section
                    id="third-party-processing"
                    index={6}
                    color="yellow"
                    title="Processing by Third Parties"
                  >
                    <p>
                      We engage trusted service providers and AI infrastructure
                      partners to process data strictly under our instructions,
                      including:
                    </p>
                    <p className="mt-4">
                      Amazon Web Services (AWS), Vercel, Convex, Stripe, Google
                      Analytics, Resend, and AI model providers OpenAI,
                      Replicate, and Fal.ai.
                    </p>
                    <p className="mt-4">
                      These parties act as processors and are contractually
                      bound by confidentiality and data protection obligations
                      consistent with the GDPR.
                    </p>
                    <p className="mt-4">
                      IMAI Studio does not use user-generated content to train
                      or fine-tune its own models, and our third-party partners
                      are not permitted to use your content for training unless
                      expressly allowed under their own published policies.
                    </p>
                  </Section>

                  <Section
                    id="automated-decision"
                    index={7}
                    color="gray"
                    title="Automated Decision-Making and Profiling"
                  >
                    <p>
                      IMAI Studio does not perform automated decision-making or
                      profiling that produces legal or similarly significant
                      effects on individuals.
                    </p>
                  </Section>

                  <Section
                    id="security-processing"
                    index={8}
                    color="blue"
                    title="Security of Processing"
                  >
                    <p>
                      We apply appropriate technical and organizational measures
                      to ensure a level of security appropriate to the risk,
                      including encryption, access controls, and regular
                      assessments of our systems.
                    </p>
                  </Section>

                  <Section
                    id="children-data"
                    index={9}
                    color="green"
                    title="Children's Data"
                  >
                    <p>
                      Our Services are not directed to children under 13 years
                      (or the minimum age required by local law).
                    </p>
                    <p className="mt-4">
                      If we become aware that a child has provided Personal Data
                      without parental consent, we will delete it promptly.
                    </p>
                  </Section>

                  <Section
                    id="addendum-updates"
                    index={10}
                    color="orange"
                    title="Updates to This Addendum"
                  >
                    <p>
                      We may amend this Addendum from time to time to reflect
                      changes in law, guidance, or our practices.
                    </p>
                    <p className="mt-4">
                      The &quot;Last Updated&quot; date at the top indicates the
                      current version.
                    </p>
                    <p className="mt-2">
                      Material changes will be communicated through our website
                      or by email where legally required.
                    </p>
                  </Section>

                  <Section
                    id="addendum-contact"
                    index={11}
                    color="red"
                    title="Contact and Complaints"
                  >
                    <p>
                      <strong>Data Controller:</strong>
                    </p>
                    <p className="mt-2">
                      IMAI Studio Inc
                      <br />
                      1388 Haight Street
                      <br />
                      San Francisco, CA 94117, USA
                      <br />
                      Email: privacy@imai.studio
                    </p>
                    <p className="mt-4">
                      If you have concerns about our handling of your Personal
                      Data, you may contact us directly.
                    </p>
                    <p className="mt-4">
                      You also have the right to lodge a complaint with your
                      local supervisory authority (for example, the CNIL in
                      France, ICO in the UK, or your national authority within
                      the EEA).
                    </p>
                  </Section>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

type SectionProps = {
  id: string;
  title: string;
  index: number;
  color:
    | 'blue'
    | 'green'
    | 'orange'
    | 'red'
    | 'purple'
    | 'indigo'
    | 'teal'
    | 'pink'
    | 'yellow'
    | 'gray'
    | 'slate';
  children: React.ReactNode;
};

function Section({ id, title, index, color, children }: SectionProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setVisible(entry.isIntersecting);
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const colorMap: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
    green:
      'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
    orange:
      'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
    red: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
    purple:
      'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400',
    indigo:
      'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400',
    teal: 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-400',
    pink: 'bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-400',
    yellow:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300',
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-500/15 dark:text-gray-400',
    slate:
      'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-400',
  };

  return (
    <section
      id={id}
      ref={ref}
      className={cn(
        'border-border/60 bg-card rounded-2xl border p-6 shadow-sm transition-all duration-500',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
      )}
    >
      <div className="mb-4 flex items-center">
        <div
          className={cn(
            'mr-3 flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
            colorMap[color]
          )}
        >
          {index}
        </div>
        <h2 className="text-xl leading-none font-semibold tracking-tight">
          {title}
        </h2>
      </div>
      <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
        {children}
      </div>
    </section>
  );
}
